const BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3000';

// ── Get stored token ───────────────────────────────────────
const getToken = () => sessionStorage.getItem('skl_token');

// ── Client-side JWT expiry check (UX hint only) ─────────────
// Decodes the payload segment without verifying the signature -- that's
// neither needed nor possible client-side. Lets the app skip a doomed
// render-app -> API-call -> 401 -> redirect round trip when we already know
// a stored token is stale. The server still verifies signature+expiry for
// real on every actual API call (see the 401 handling in `call` below).
export function decodeJwtPayload(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function isTokenExpired(token) {
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) return true;
  return payload.exp <= Date.now() / 1000;
}

const call = async (method, path, body = null, signal) => {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : null,
    signal,
  });

  // Token expired or invalid
  if (res.status === 401) {
    sessionStorage.removeItem('skl_token');
    sessionStorage.removeItem('skl_user');
    window.location.href = '/auth/login';
    throw new Error('Session expired — redirecting to login');
  }

  const data = await res.json();
  if (!res.ok) {
    // Attach the parsed body so callers can branch on structured fields
    // (e.g. `duplicate: true` from POST /api/requests) without having to
    // pattern-match the message string.
    const err = new Error(data.error || 'Request failed');
    err.body = data;
    throw err;
  }
  return data;
};

export const api = {
  // Auth
  me: () => call('GET', '/auth/me'),
  logout: () => call('GET', '/auth/logout'),

  // Health
  health: () => fetch(`${BASE}/api/health`).then(r => r.json()),

  // Dashboard
  dashboard: (monthKey, signal) => call('GET', `/api/dashboard/${monthKey}`, null, signal),

  // Plans
  listSharepointFiles: (monthKey) => call('GET', `/api/plans/sharepoint-files${monthKey ? `?monthKey=${monthKey}` : ''}`),
  loadPlan: (body) => call('POST', '/api/plans/load', body),
  listPlans: () => call('GET', '/api/plans'),
  getPlan: (monthKey) => call('GET', `/api/plans/${monthKey}`),
  closeCheck: (planId) => call('GET', `/api/plans/${planId}/close-check`),
  resetPlan: (monthKey) => call('DELETE', `/api/plans/reset/${monthKey}`),
  reversePayment: (body) => call('POST', '/api/requests/reverse', body),
  closePlan: (planId, actor) => call('POST', `/api/plans/${planId}/close`, { actor }),

  // Requests
  listRequests: (monthKey, status) => {
    const params = new URLSearchParams();
    if (monthKey) params.set('monthKey', monthKey);
    if (status) params.set('status', status);
    return call('GET', `/api/requests?${params}`);
  },
  createRequest: (body) => call('POST', '/api/requests', body),
  tripsFor: (noTruk, tipe) => call('GET', `/api/ritase/trips?noTruk=${encodeURIComponent(noTruk)}&tipe=${encodeURIComponent(tipe)}`),
  approveRequest: (id) => call('PATCH', `/api/requests/${id}/approve`, {}),
  rejectRequest: (id, note) => call('PATCH', `/api/requests/${id}/reject`, { rejectionNote: note }),
  bulkApproveRequests: (ids) => call('PATCH', '/api/requests/bulk-approve', { ids }),
  fixFlaggedRequest: (id, noTruk) => call('PATCH', `/api/requests/${id}/fix-flag`, { noTruk }),

  // Reopen — correcting the amount on an already-approved request
  reopenRequest: (id, newAmount, reason) => call('POST', `/api/requests/${id}/reopen`, { newAmount, reason }),
  listPendingReopens: () => call('GET', '/api/requests/reopen/pending'),
  approveReopen: (id) => call('PATCH', `/api/requests/reopen/${id}/approve`, {}),
  rejectReopen: (id, note) => call('PATCH', `/api/requests/reopen/${id}/reject`, { note }),

  // Reverse request — staff-initiated, director-approved cancellation of a done request
  requestReverse: (id, reason) => call('POST', `/api/requests/${id}/reverse-request`, { reason }),
  listPendingReverseRequests: () => call('GET', '/api/requests/reverse-requests/pending'),
  approveReverseRequest: (id) => call('PATCH', `/api/requests/reverse-requests/${id}/approve`, {}),
  rejectReverseRequest: (id, note) => call('PATCH', `/api/requests/reverse-requests/${id}/reject`, { note }),

  // Amendments
  listAmendments: (monthKey, status) => {
    const params = new URLSearchParams();
    if (monthKey) params.set('monthKey', monthKey);
    if (status) params.set('status', status);
    return call('GET', `/api/amendments?${params}`);
  },
  createAmendment: (body) => call('POST', '/api/amendments', body),
  approveAmendment: (id) => call('PATCH', `/api/amendments/${id}/approve`, {}),
  rejectAmendment: (id, note) => call('PATCH', `/api/amendments/${id}/reject`, { rejectionNote: note }),
  bulkApproveAmendments: (ids, batchId) => call('PATCH', '/api/amendments/bulk-approve', { ids, batchId }),
  bulkRejectAmendments: (ids, note) => call('PATCH', '/api/amendments/bulk-reject', { ids, rejectionNote: note }),
  amendmentHistory: (monthKey) => call('GET', `/api/amendments/history/${monthKey}`),

  // Ritase
  ritaseSummary: (monthKey) => call('GET', `/api/ritase/summary/${monthKey}`),
  ritaseLog: (monthKey) => call('GET', `/api/ritase?monthKey=${monthKey}`),

  // P&L
  listPL: () => call('GET', '/api/pl'),
  getPL: (monthKey) => call('GET', `/api/pl/${monthKey}`),
  savePL: (body) => call('POST', '/api/pl', body),
  lockPL: (monthKey) => call('PATCH', `/api/pl/${monthKey}/lock`, {}),
  updatePLNote: (monthKey, note) => call('PATCH', `/api/pl/${monthKey}/note`, { note }),

  // Four-tab redesign (2026-10-01)
  history: (monthKey) => call('GET', `/api/v2/history/${monthKey}`),
  withdrawAmendment: (id) => call('PATCH', `/api/amendments/${id}/withdraw`, {}),
  changeRekening: (lineId, rekening, monthKey, reason) => call('PATCH', `/api/v2/lines/${lineId}/rekening`, { rekening, monthKey, reason }),
  changeTruck: (lineId, noTruk, monthKey, reason) => call('PATCH', `/api/v2/lines/${lineId}/truck`, { noTruk, monthKey, reason }),
  bankBalances: (monthKey) => call('GET', `/api/v2/bank/${monthKey}`),
  accurate: (monthKey, signal) => call('GET', `/api/v2/accurate/${monthKey}`, null, signal),
  bankMatch: (monthKey, signal, account) => call('GET', `/api/v2/bankmatch/${monthKey}${account ? `?account=${account}` : ''}`, null, signal),
  overdueSnoozes: (planId) => call('GET', `/api/v2/overdue/snoozes/${planId}`),
  biarkan: (lineId) => call('POST', `/api/v2/overdue/${lineId}/biarkan`, {}),
  undoAuto: (requestId, reason) => call('POST', `/api/v2/auto/${requestId}/undo`, { reason }),
  dismissBankItem: (monthKey, key, reason) => call('POST', `/api/v2/bankmatch/${monthKey}/dismiss`, { key, reason }),
  undoDismiss: key => call('POST', '/api/v2/bankmatch/dismiss/undo', { key }),
  receivables: (monthKey) => call('GET', `/api/v2/receivables/${monthKey}`),
  armada: (monthKey, signal) => call('GET', `/api/v2/armada/${monthKey}`, null, signal),
  trucks: signal => call('GET', '/api/v2/trucks', null, signal),
  dismissForecast: (monthKey, ev, reason) => call('POST', `/api/v2/forecast/${monthKey}/dismiss`, { truck: ev.truck, kind: ev.kind, date: ev.date, label: ev.label, reason }),
  undoForecastDismiss: (id) => call('POST', `/api/v2/forecast/dismiss/${id}/undo`),
  // 3 months ahead, BCA (server/services/draftPlan.js › outlook)
  outlook: (signal, fresh) => call('GET', `/api/v2/outlook${fresh ? '?fresh=1' : ''}`, null, signal),
  forecast: (monthKey, signal, fresh) => call('GET', `/api/v2/forecast/${monthKey}${fresh ? '?fresh=1' : ''}`, null, signal),
  truckAccount: (monthKey, no, signal) => call('GET', `/api/v2/armada/${monthKey}/truk/${no}`, null, signal),
  analisisKas: (monthKey, signal) => call('GET', `/api/v2/analisis-kas/${monthKey}`, null, signal),
  analisis: (monthKey, signal) => call('GET', `/api/v2/analisis/${monthKey}`, null, signal),
  ritasePace: (monthKey, signal) => call('GET', `/api/v2/ritase-pace/${monthKey}`, null, signal),
  piutang: (monthKey, signal) => call('GET', `/api/v2/piutang/${monthKey}`, null, signal),
  accurateSyncStatus: () => call('GET', '/api/v2/accurate-sync'),
  runAccurateSync: (full = false) => call('POST', '/api/v2/accurate-sync', { full }),
  bayangan: (signal) => call('GET', '/api/v2/bayangan', null, signal),
  runBayangan: () => call('POST', '/api/v2/bayangan/run'),
  setBankBalance: (monthKey, tanggal, amount) => call('PUT', `/api/v2/bank/${monthKey}/${tanggal}`, { amount }),
};
